import { FC, useContext, useEffect, useMemo } from 'react';
import { createGQLDataContext } from 'utils/createDataContext';
import { useGQLQuery } from 'hooks';
import {
  OrganizationSettingsPageSenorityMethodQuery,
  OrganizationSettingsPageSenorityMethodQueryVariables,
  SeniorityMethodEnum,
} from 'types/graphql';
import { GQLAPIContextType } from 'types/common.types';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { QUERY_organizationSettingsPageSeniorityMethod } from 'queries';
import { UseGQLQueryConfig } from 'hooks/useGQLQuery/useGQLQuery.types';

export const SeniorityRankingDataContext =
  createGQLDataContext<GQLAPIContextType<OrganizationSettingsPageSenorityMethodQuery>>();

export const SeniorityRankingDataProvider: FC = ({ children }) => {
  const locationId = getCurrentlySelectedLocation();

  const senorityMethodConfigMemo: UseGQLQueryConfig<
    OrganizationSettingsPageSenorityMethodQuery,
    OrganizationSettingsPageSenorityMethodQuery,
    OrganizationSettingsPageSenorityMethodQueryVariables
  > = useMemo(
    () => ({
      key: 'FETCH_SENIORITY_RANKING_PAGE',
      query: QUERY_organizationSettingsPageSeniorityMethod,
      variables: {
        locationId: Number(locationId?.value),
      },
    }),
    [locationId],
  );

  const { data, fetch, ...restQuery } =
    useGQLQuery<
      OrganizationSettingsPageSenorityMethodQuery,
      OrganizationSettingsPageSenorityMethodQuery,
      OrganizationSettingsPageSenorityMethodQueryVariables
    >(senorityMethodConfigMemo);

  useEffect(() => {
    if (!restQuery?.called && locationId) fetch();
  }, [locationId]); // eslint-disable-line

  const contextValues = {
    data: {
      fetchCommunity: {
        organization: {
          seniorityMethod:
            data?.fetchCommunity?.organization?.seniorityMethod ||
            SeniorityMethodEnum.years_months_days,
          updatedAt: data?.fetchCommunity?.organization?.updatedAt,
          createdAt: data?.fetchCommunity?.organization?.createdAt,
          relayId: data?.fetchCommunity?.organization?.relayId,
          lastEditedBy: data?.fetchCommunity?.organization?.lastEditedBy,
        },
      },
    },
    fetch,
    ...restQuery,
  };
  return (
    <SeniorityRankingDataContext.Provider value={contextValues}>
      {children}
    </SeniorityRankingDataContext.Provider>
  );
};

export const useSeniorityRankingDataContext =
  (): GQLAPIContextType<OrganizationSettingsPageSenorityMethodQuery> =>
    useContext(SeniorityRankingDataContext);
